<script setup lang="ts">
import {computed} from 'vue';
import {useReporting} from '@utilities/reports/composables/useReporting';
import {useTableHighlight} from '@utilities/reports/composables/useTableHighlight';
import KPIHeader from "@components/reports/KPI/KPIHeader.vue";

// Initialize store and composables
const {daysInMonth, hasDataForDay, isEodSubmitted, isOpen, getValueForDay, reportData} = useReporting('kpi');
const {setHoveredRow, setHoveredCol, clearHoveredRow, clearHoveredCol, isRowHovered, isColHovered} = useTableHighlight();

// Transform KPI data for table display
const tableData = computed(() => {
  if (!reportData.value || !reportData.value.data) return [];

  const rows = [];
  const data = reportData.value.data;

  // Define human-readable names for indicators
  const indicatorNames = {
    open: 'Open',
    eodSubmitted: 'EOD Submitted',
    totalSales: 'Total Sales',
    numSales: 'Number of Sales',
    cash: 'Cash',
    cc: 'CC',
    numServices: 'Services Performed',
    numSmogs: 'Number of Smogs',
    totalServiceSales: 'Total Service Sales',
    totalSmogSales: 'Total Smog Sales',
    numRejects: 'Number of Rejects',
    numIssues: 'Number of Issues',
    servicePercent: 'Service Percent',
    serviceSalesPercent: 'Service Sales Percent'
  };

  // Create a row for each indicator
  Object.keys(data).forEach(indicator => {
    if (['dates', 'open', 'eodSubmitted'].includes(indicator)) return;

    const row = {
      key: indicator,
      indicator: indicatorNames[indicator] ?? indicator,
      overall: data[indicator].overall ?? 'N/A',
      daily: data[indicator].daily ?? []
    };

    rows.push(row);
  });

  return rows;
});

</script>

<template>
  <div class="flex flex-col gap-4 w-full px-4 mt-4 items-center">
    <KPIHeader :summary="reportData.summary"/>

    <!-- Table container with horizontal scroll -->
    <div class="w-full justify-start">
      <div
          class="relative overflow-x-auto max-h-[calc(100vh-200px)] overflow-y-auto [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-track]:bg-slate-100 [&::-webkit-scrollbar-thumb]:bg-slate-500 [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb:hover]:bg-slate-500 border-slate-200">
        <!-- Table with Tailwind classes -->
        <table class="w-full border-collapse border border-gray-300 table-fixed">
          <!-- Table Header -->
          <thead>
          <tr>
            <!-- Fixed "Overall" column -->
            <th
                class="sticky left-0 z-20 bg-white border border-gray-300 p-2 w-32 text-center"
                @mouseover="setHoveredCol(0)"
                @mouseout="clearHoveredCol()"
            >
              Overall
            </th>

            <!-- Fixed "KPI" column -->
            <th
                class="sticky left-32 z-20 bg-primary-500 text-white border border-gray-300 p-2 w-48 text-center"
                @mouseover="setHoveredCol(1)"
                @mouseout="clearHoveredCol()"
            >
              KPI
            </th>

            <!-- Day number columns -->
            <th
                v-for="day in daysInMonth"
                :key="day"
                class="border border-gray-300 p-2 w-24 text-center"
                :class="{
                'bg-slate-100': (isColHovered(day + 1) && isEodSubmitted(day)) || !isOpen(day),
                'bg-slate-400': !hasDataForDay(day),
                'bg-amber-100': hasDataForDay(day) && !isEodSubmitted(day) && isOpen(day) && !isColHovered(day + 1),
                'bg-amber-200': (!isEodSubmitted(day) && isColHovered(day + 1))
              }"
                @mouseover="setHoveredCol(day + 1)"
                @mouseout="clearHoveredCol()"
            >
              {{ day }}
            </th>
          </tr>
          </thead>

          <!-- Table Body -->
          <tbody>
          <tr
              v-for="(row, rowIndex) in tableData"
              :key="row.key"
              :class="{ 'bg-slate-100': isRowHovered(rowIndex) }"
              @mouseover="setHoveredRow(rowIndex)"
              @mouseout="clearHoveredRow()"
          >
            <!-- Overall value column (fixed) -->
            <td
                class="sticky left-0 z-10 bg-white border border-gray-300 p-2 w-20 text-center"
                :class="{ 'bg-slate-100': isColHovered(0) || isRowHovered(rowIndex) }"
                @mouseover="setHoveredCol(0)"
                @mouseout="clearHoveredCol()"
            >
              {{ row.overall }}
            </td>

            <!-- Indicator name column (fixed) -->
            <td
                class="sticky left-32 z-10 bg-primary-500 font-normal text-center border border-gray-300 p-2 w-48 text-white"
                @mouseover="setHoveredCol(1)"
                @mouseout="clearHoveredCol()"
            >
              {{ row.indicator }}
            </td>

            <!-- Daily value columns -->
            <td
                v-for="day in daysInMonth"
                :key="day"
                class="border border-gray-300 p-2 w-24 text-center overflow-hidden"
                :class="{
                'bg-slate-100': (isColHovered(day + 1) && isEodSubmitted(day)) || (isRowHovered(rowIndex) && isEodSubmitted(day)) || !isOpen(day),
                'bg-slate-400': !hasDataForDay(day),
                'bg-amber-100': hasDataForDay(day) && !isEodSubmitted(day) && isOpen(day) && !isColHovered(day + 1) && !isRowHovered(rowIndex),
                'bg-amber-200': (!isEodSubmitted(day) && (isRowHovered(rowIndex) || isColHovered(day + 1)))
              }"
                @mouseover="setHoveredCol(day + 1)"
                @mouseout="clearHoveredCol()"
            >
              <template
                  v-if="isEodSubmitted(day) || row.key === 'numIssues' || ![0, '$0.00', '0%'].includes(getValueForDay(row, day))">
                <!-- Display value if EOD is submitted or if it's a specific indicator -->
                {{ getValueForDay(row, day) }}
              </template>
              <i v-else-if="isOpen(day)" class="pi pi-exclamation-circle text-amber-600"/>
              <i v-else class="pi pi-circle text-slate-300"></i>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

